import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { AdminCreditsAPI } from "../../api/adminCreditsApi";
import { extractErrorMessage } from "../../utils/extractErrorMessage";

function formatDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  } catch {
    return String(v);
  }
}

export default function AdminUserCreditsPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [wallets, setWallets] = useState([]);
  const [txs, setTxs] = useState([]);

  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState("Support");
  const [reference, setReference] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const canSearch = useMemo(() => query.trim().length >= 2, [query]);

  const doSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const res = await AdminCreditsAPI.searchUsers(query.trim());
      const items = res.data?.items ?? res.data?.users ?? res.data ?? [];
      setUsers(Array.isArray(items) ? items : []);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const loadUserData = async (user) => {
    setSelectedUser(user);
    setWallets([]);
    setTxs([]);
    setLoadingUserData(true);
    setError("");
    setInfo("");
    try {
      const [wRes, tRes] = await Promise.all([
        AdminCreditsAPI.getUserWallets(user.id),
        AdminCreditsAPI.getUserTransactions(user.id),
      ]);
      setWallets(wRes.data?.items ?? wRes.data ?? []);
      setTxs(tRes.data?.items ?? tRes.data ?? []);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoadingUserData(false);
    }
  };

  useEffect(() => {
    // petit confort: si tu colles un email et appuies sur Entrée
    const handler = (e) => {
      if (e.key === "Enter") doSearch();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
   
  }, [query, canSearch]);

  const doCreditOrDebit = async (mode) => {
    if (!selectedUser) return;
    const n = Number(minutes);
    if (!Number.isFinite(n) || n <= 0) {
      setError("Minutes invalides.");
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const payload = {
        minutes: Math.floor(n),
        reason: reason?.trim() || "Support",
        reference: reference?.trim() || null,
      };
      if (mode === "credit") {
        await AdminCreditsAPI.adminCredit(selectedUser.id, payload);
        setInfo(t('admin.credit_applied'));
      } else {
        await AdminCreditsAPI.adminDebit(selectedUser.id, payload);
        setInfo(t('admin.debit_applied'));
      }
      await loadUserData(selectedUser);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const doResetPassword = async () => {
    if (!selectedUser) return;
    if (!window.confirm(t('admin.reset_password_confirm'))) return;

    setLoading(true);
    setError("");
    setInfo("");
    try {
      await AdminCreditsAPI.resetUserPassword(selectedUser.id);
      setInfo("Mot de passe réinitialisé.");
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-3">
        <h1 className="text-xl font-semibold">{t('admin.user_credits_title')}</h1>
        <div className="text-sm text-gray-600">
          {t('admin.user_credits_desc')}
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-800 rounded">
          {error}
        </div>
      )}
      {info && (
        <div className="mb-3 p-2 bg-green-50 border border-green-200 text-green-800 rounded">
          {info}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flex-1">
          <label className="block text-sm mb-1">Recherche (email / username)</label>
          <div className="flex gap-2">
            <input
              className="border rounded px-2 py-2 w-full"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ex: luke@gmail.com"
            />
            <button
              disabled={loading || !canSearch}
              className="px-4 py-2 rounded bg-black text-white"
              onClick={doSearch}
            >
              Chercher
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1">{t('admin.min_chars')}</div>
        </div>

        <div className="w-full md:w-[420px] border rounded p-3">
          <div className="font-medium mb-2">{t('admin.support_actions')}</div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="block text-xs mb-1">Minutes</label>
              <input
                type="number"
                className="border rounded px-2 py-1 w-full"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                min={1}
              />
            </div>
            <div>
              <label className="block text-xs mb-1">{t('admin.reference_opt')}</label>
              <input
                className="border rounded px-2 py-1 w-full"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="TICKET-123"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-xs mb-1">Motif</label>
            <input
              className="border rounded px-2 py-1 w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Support / Compensation / Correction..."
            />
          </div>

          <div className="flex gap-2">
            <button
              disabled={loading || !selectedUser}
              className="flex-1 px-3 py-2 rounded bg-black text-white"
              onClick={() => doCreditOrDebit("credit")}
            >
              Crédit
            </button>
            <button
              disabled={loading || !selectedUser}
              className="flex-1 px-3 py-2 rounded border"
              onClick={() => doCreditOrDebit("debit")}
            >
              Débit
            </button>
          </div>

          <button
            disabled={loading || !selectedUser}
            className="mt-2 w-full px-3 py-2 rounded border"
            onClick={doResetPassword}
          >
            Reset password
          </button>

          {!selectedUser && (
            <div className="text-xs text-gray-500 mt-2">
              {t('admin.select_user_hint')}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Users list */}
        <div className="border rounded">
          <div className="p-2 border-b bg-gray-50 font-medium">{t('admin.results')}</div>
          <div className="max-h-[65vh] overflow-auto">
            {users.map((u) => (
              <button
                key={u.id}
                className={`w-full text-left px-3 py-2 border-b hover:bg-gray-50 ${
                  selectedUser?.id === u.id ? "bg-gray-100" : ""
                }`}
                onClick={() => loadUserData(u)}
              >
                <div className="font-medium">
                  {u.username || "(no username)"}{" "}
                  <span className="text-xs text-gray-500">#{u.id}</span>
                </div>
                <div className="text-sm text-gray-700">{u.email}</div>
                <div className="text-xs text-gray-500">
                  role={u.role} • active={String(u.is_active)}
                </div>
              </button>
            ))}
            {users.length === 0 && (
              <div className="p-3 text-gray-500">
                {t('admin.no_results')}
              </div>
            )}
          </div>
        </div>

        {/* Wallets */}
        <div className="border rounded">
          <div className="p-2 border-b bg-gray-50 font-medium">{t('admin.wallets_label')}</div>
          <div className="p-2 text-sm text-gray-600">
            {selectedUser ? (
              loadingUserData ? "Chargement..." : `Utilisateur: ${selectedUser.email}`
            ) : (
              "{t('admin.select_user')}"
            )}
          </div>
          <div className="max-h-[55vh] overflow-auto">
            {wallets.map((w) => (
              <div key={w.id} className="border-t p-3">
                <div className="flex justify-between items-center">
                  <div className="font-medium">
                    Wallet #{w.id}{" "}
                    <span className="text-xs text-gray-500">
                      ({w.is_active ? "active" : "inactive"})
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold">{w.remaining_minutes}</span>{" "}
                    <span className="text-gray-500">min</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  total={w.total_minutes} • used={w.used_minutes}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  created: {formatDate(w.created_at)}
                </div>
                {/* Si tu ajoutes tags au wallet en backend, tu pourras afficher ici w.tags */}
              </div>
            ))}
            {selectedUser && !loadingUserData && wallets.length === 0 && (
              <div className="p-3 text-gray-500">{t('admin.no_wallet')}</div>
            )}
          </div>
        </div>

        {/* Transactions */}
        <div className="border rounded">
          <div className="p-2 border-b bg-gray-50 font-medium">{t('admin.transactions_label')}</div>
          <div className="max-h-[65vh] overflow-auto">
            {txs.map((t) => (
              <div key={t.id} className="border-t p-3">
                <div className="flex justify-between">
                  <div className="font-medium">
                    {t.type || "tx"}{" "}
                    <span className="text-xs text-gray-500">#{t.id}</span>
                  </div>
                  <div className={`text-sm font-semibold ${t.minutes >= 0 ? "" : ""}`}>
                    {t.minutes} min
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  source={t.source || "-"} • created: {formatDate(t.created_at)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  order_id={t.order_id || "-"} • external_id={t.external_id || "-"}
                </div>
                {t.message && (
                  <div className="text-xs text-gray-700 mt-1">{t.message}</div>
                )}
              </div>
            ))}
            {selectedUser && !loadingUserData && txs.length === 0 && (
              <div className="p-3 text-gray-500">{t('admin.no_transactions')}</div>
            )}
            {!selectedUser && (
              <div className="p-3 text-gray-500">Sélectionne un utilisateur.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
